import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

const STORAGE_KEY = 'ipm_lang';
const SUPPORTED_LANGUAGES = ['en', 'es', 'ar', 'it', 'bm', 'man', 'fr', 'pt', 'zh'];

/* Se ejecuta AL IMPORTAR el módulo (ver `detectedLang` abajo), así que no puede
   fallar nunca: si revienta aquí, revienta el arranque de la app entera. `window`
   puede existir y `localStorage` no —o existir y lanzar— en más sitios de los que
   parece: Safari en navegación privada, WebViews embebidas, un iframe con origen
   opaco y el entorno de los tests. Antes se comprobaba solo `window` y se usaba
   `localStorage` a pelo; con eso, importar cualquier módulo que arrastre el i18n
   tiraba «Cannot read properties of undefined (reading 'getItem')». */
const getStoredLanguage = (): string => {
  try {
    if (typeof localStorage !== 'undefined') {
      const storedLang = localStorage.getItem(STORAGE_KEY);
      if (storedLang) return storedLang;
    }
  } catch { /* almacenamiento bloqueado: se sigue con el idioma del navegador */ }
  try {
    if (typeof navigator !== 'undefined' && typeof navigator.language === 'string') {
      const browserLang = navigator.language.split('-')[0];
      if (SUPPORTED_LANGUAGES.includes(browserLang)) return browserLang;
    }
  } catch { /* idem */ }
  return 'es';
};

const detectedLang = getStoredLanguage();

// Only load the detected language synchronously; lazy-load others on demand
const localeLoaders: Record<string, () => Promise<{ default: Record<string, unknown> }>> = {
  en: () => import('./locales/en.json'),
  es: () => import('./locales/es.json'),
  ar: () => import('./locales/ar.json'),
  it: () => import('./locales/it.json'),
  bm: () => import('./locales/bm.json'),
  man: () => import('./locales/man.json'),
  fr: () => import('./locales/fr.json'),
  pt: () => import('./locales/pt.json'),
  zh: () => import('./locales/zh.json'),
};

// Eagerly import only the detected language to keep initial bundle small
let initialResource: Record<string, unknown> | undefined;

// We use a top-level await workaround: load the initial language before init
const initPromise = (async () => {
  const mod = await localeLoaders[detectedLang]();
  initialResource = mod.default ?? mod;

  await i18n
    .use(initReactI18next)
    .init({
      resources: {
        [detectedLang]: { translation: initialResource },
      },
      lng: detectedLang,
      fallbackLng: 'en',
      interpolation: { escapeValue: false },
      initImmediate: true,
      returnEmptyString: false,
      react: {
        useSuspense: false,
        bindI18n: 'languageChanged loaded initialized',
        bindI18nStore: 'added removed',
      },
    });
})();

// Lazy-load a language bundle when switching
export async function loadLanguage(lng: string) {
  if (!localeLoaders[lng]) return;
  if (i18n.hasResourceBundle(lng, 'translation')) return;
  const mod = await localeLoaders[lng]();
  i18n.addResourceBundle(lng, 'translation', mod.default ?? mod, true, true);
}

// Override changeLanguage to lazy-load first
const originalChangeLanguage = i18n.changeLanguage.bind(i18n);
// eslint-disable-next-line @typescript-eslint/no-explicit-any
(i18n as any).changeLanguage = async (lng?: string, ...args: any[]) => {
  if (lng && localeLoaders[lng]) {
    await loadLanguage(lng);
  }
  return originalChangeLanguage(lng, ...args);
};

/* Mismo motivo que en `getStoredLanguage`, pero peor: esto escribe dentro de un
   handler de i18next, así que una excepción (Safari privado, cuota llena, iframe
   con origen opaco) no se queda aquí: sube por `emit` y rompe el propio cambio de
   idioma. Recordar la preferencia es un extra; poder cambiar de idioma no lo es. */
i18n.on('languageChanged', (lng) => {
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(STORAGE_KEY, lng);
    }
  } catch { /* no se puede recordar la preferencia: el idioma cambia igual */ }
});

// If the fallback (en) isn't the detected language, preload it in background
if (detectedLang !== 'en') {
  initPromise.then(() => loadLanguage('en'));
}

export default i18n;
